import CachedIcon from "@mui/icons-material/Cached";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import prettyBytes from "pretty-bytes";
import { useEffect, useState } from "react";
import { deleteFile, downloadFile, getFiles } from "../../api/file";
import { useTranslationHook } from "../../hooks";
import { ProjectFile } from "../../types/model/file";
import { formatDate } from "../../utils";
import { FileUpload } from "./FileUpload";

interface FilesPropos {
  accountId: number;
  projectId?: number;
  activityId?: number;
  procedureId?: number;
  readOnly: boolean;
}
export default function Files({
  accountId,
  projectId,
  activityId,
  procedureId,
  readOnly
}: Readonly<FilesPropos>) {
  const [loadingFiles, setLoadingFiles] = useState(false);
  
  const { t } = useTranslationHook();
  const [search, setSearch] = useState("");
  const [filteredFiles, setFilteredFiles] = useState<ProjectFile[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<ProjectFile[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const session = JSON.parse(localStorage.getItem("session") || "{}");
  const roles = session.role || [];
  const isManager = session.isManager || roles.includes("ACCOUNT_MANAGER");

  const fetchExistingFiles = async () => {
    setLoadingFiles(true);
    try {
      const data = await getFiles(accountId, projectId, activityId, procedureId);
      setUploadedFiles(data);
      setFilteredFiles(data);
    } catch (error) {
      enqueueSnackbar("Erro ao buscar ficheiros", { variant: "error" });
    } finally {
      setLoadingFiles(false);
    }
  }

  const handleDownload = async (fileId: number, filename: string) => {
    try {
      await downloadFile(fileId, filename);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteExistingFile = async (fileId: number) => {
    setLoadingFiles(true);
    try {
      await deleteFile(fileId);
      await fetchExistingFiles();
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleOnFileUpdated = () => {
    fetchExistingFiles();
  };

  useEffect(() => {
    const lower = search.toLowerCase();
    const filtered = uploadedFiles.filter((file) =>
      file.filename ? file.originalName?.toLowerCase().includes(lower) : false
    );
    setFilteredFiles(filtered);
    setPage(0);
  }, [search, uploadedFiles]);

  useEffect(() => {
    fetchExistingFiles();
  }, []);

  return (
    <>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            fullWidth
            label={t("generic.search")}
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mr: 2 }}
          />
          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={fetchExistingFiles}>
              <CachedIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          {loadingFiles ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("generic.file-name")}</TableCell>
                  <TableCell>{t("generic.created")}</TableCell>
                  <TableCell>{t("generic.account")}</TableCell>
                  <TableCell>{t("generic.size")}</TableCell>
                  <TableCell align="right">
                    {t("generic.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredFiles
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>{file.originalName}</TableCell>
                      <TableCell>
                        {file.createdOn
                          ? formatDate(file.createdOn.toString())
                          : ""}
                      </TableCell>
                      <TableCell>{file.accountName}</TableCell>
                      <TableCell>{prettyBytes(file.size)}</TableCell>

                      <TableCell align="right">
                        <IconButton
                          onClick={() =>
                            handleDownload(
                              file.id,
                              file.originalName ? file.originalName : ""
                            )
                          }
                          color="primary"
                          size="small"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>

                        {isManager && !readOnly && ( 
                          <IconButton
                            onClick={() => handleDeleteExistingFile(file.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredFiles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {isManager && !readOnly && (
        <FileUpload
          accountId={accountId}
          projectId={projectId}
          activityId={activityId}
          procedureId={procedureId}
          onFilesUpdated={handleOnFileUpdated}
        />
      )}
    </>
  );
}
